import React, { useEffect, useMemo, useState } from 'react';
import {
  ALL_MAKES,
  CAR_CATALOG,
  modelsForMake,
  type CarCatalogEntry,
} from '@parkproof/shared';
import { ChevronLeft, ChevronRight, Search as SearchIcon } from './Icon';

interface CarPickerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called when the user picks a model from the catalog. */
  onSelect: (entry: CarCatalogEntry) => void;
  /** Called when the user opts out and wants to enter a custom car. */
  onSelectCustom: () => void;
}

/**
 * Web counterpart to app/src/components/sheets/CarPickerSheet.tsx.
 * Two-step Make → Model picker with a "Not listed — enter manually" escape.
 * Behaviour and copy match the iOS sheet so the two flows stay in lockstep.
 */
export const CarPickerDialog: React.FC<CarPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  onSelectCustom,
}) => {
  const [step, setStep] = useState<'make' | 'model'>('make');
  const [selectedMake, setSelectedMake] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const reset = () => {
    setStep('make');
    setSelectedMake(null);
    setQuery('');
  };

  // Reset state every time the dialog opens; lock body scroll while it's mounted.
  useEffect(() => {
    if (!open) return;
    reset();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePickMake = (make: string) => {
    setSelectedMake(make);
    setStep('model');
    setQuery('');
  };

  const handlePickModel = (entry: CarCatalogEntry) => {
    onSelect(entry);
    reset();
  };

  // Make-search also surfaces models matching the query — typing "RAV4" reveals Toyota.
  const filteredMakes = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_MAKES;
    const directMakes = ALL_MAKES.filter((m) => m.toLowerCase().includes(q));
    const fromModels = Array.from(
      new Set(
        CAR_CATALOG.filter((c) => c.model.toLowerCase().includes(q)).map((c) => c.make),
      ),
    );
    return Array.from(new Set([...directMakes, ...fromModels]));
  }, [query]);

  const filteredModels = useMemo(() => {
    if (!selectedMake) return [];
    const all = modelsForMake(selectedMake);
    const q = query.trim().toLowerCase();
    return q ? all.filter((m) => m.model.toLowerCase().includes(q)) : all;
  }, [selectedMake, query]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center sm:items-center sm:p-s5"
      role="dialog"
      aria-modal="true"
      aria-label={step === 'make' ? 'Choose your car' : `Choose a ${selectedMake} model`}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={handleClose}
        className="absolute inset-0 bg-deep/40"
      />

      <div className="relative flex flex-col w-full h-full sm:h-[min(720px,90vh)] sm:max-w-phone sm:rounded-xl bg-page overflow-hidden">
        <div className="flex items-center px-s4 py-s3 border-b border-rule-soft">
          <div className="min-w-[60px] flex items-center">
            {step === 'model' ? (
              <button
                type="button"
                onClick={() => {
                  setStep('make');
                  setQuery('');
                }}
                className="w-9 h-9 -ml-2 flex items-center justify-center"
                aria-label="Back to makes"
              >
                <ChevronLeft size={18} />
              </button>
            ) : null}
          </div>
          <div className="flex-1 text-center font-serif font-medium text-[16px] text-ink1 truncate px-s2">
            {step === 'make' ? 'Choose your car' : selectedMake}
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="min-w-[60px] text-right text-[13px] font-sans font-medium text-ink2"
          >
            Cancel
          </button>
        </div>

        <div className="px-s5 py-s4">
          <div className="flex items-center gap-2 px-s4 py-[11px] bg-card border border-rule rounded-pill">
            <SearchIcon size={14} color="#8B8680" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                step === 'make' ? 'Search make or model' : `Search ${selectedMake} models`
              }
              autoCapitalize="words"
              autoCorrect="off"
              className="flex-1 bg-transparent text-[14px] font-sans text-ink1 placeholder-ink3 outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {step === 'make' ? (
            <>
              {filteredMakes.length === 0 ? (
                <div className="p-s7 text-center text-[13px] text-ink3 font-sans">
                  No matches. Try a different name.
                </div>
              ) : (
                <ul>
                  {filteredMakes.map((make, idx) => (
                    <li key={make}>
                      <button
                        type="button"
                        onClick={() => handlePickMake(make)}
                        className="w-full flex items-center px-s5 py-s4 bg-page text-left"
                      >
                        <span className="flex-1 text-[15px] font-sans text-ink1">{make}</span>
                        <ChevronRight />
                      </button>
                      {idx < filteredMakes.length - 1 ? (
                        <div className="h-px bg-rule-soft ml-s5" />
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="button"
                onClick={() => {
                  reset();
                  onSelectCustom();
                }}
                className="block mx-auto mt-s3 py-s5 px-s5 w-full text-center text-[13px] font-sans font-medium text-ink1 border-t border-rule underline"
              >
                My car isn't listed — enter manually
              </button>
            </>
          ) : filteredModels.length === 0 ? (
            <div className="p-s7 text-center text-[13px] text-ink3 font-sans">
              No matches. Try a different name.
            </div>
          ) : (
            <ul>
              {filteredModels.map((m, idx) => (
                <li key={`${m.make}-${m.model}`}>
                  <button
                    type="button"
                    onClick={() => handlePickModel(m)}
                    className="w-full flex items-center px-s5 py-s4 bg-page text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-sans text-ink1">{m.model}</div>
                      <div className="font-mono text-[11px] tracking-wider text-ink3 mt-0.5">
                        {m.type.toUpperCase()} · {m.heightM.toFixed(2)} M
                      </div>
                    </div>
                    <ChevronRight />
                  </button>
                  {idx < filteredModels.length - 1 ? (
                    <div className="h-px bg-rule-soft ml-s5" />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
